// src/components/sourcex/DefaultValueInput.jsx
//
// One answer piece's (or table column's) "default value" control.
//
// Lifted out of QuestionBuilder.jsx unchanged so the composite-question
// editor and the table grid editor share the exact same control rather than
// each growing its own copy — a table cell and a composite piece default are
// the same thing, and they must keep behaving identically.

import { inputCls } from "./QuestionField";

// What this control is CALLED, and the chip it announces itself with — in ONE
// place, because it appears twice: as a box beside a write-in answer
// (QuestionBuilder's PieceCard) and as the clickable ○/☐ beside each choice
// (OptionsEditor). Those are two shapes of the same feature, so they have to
// carry the same name and the same blue, or a coordinator who has met one has
// no reason to recognise the other.
//
// It lives here rather than in QuestionBuilder because QuestionBuilder imports
// OptionsEditor — a constant exported from there and imported back would be a
// circular import for the sake of one string.
//
// It used to read "Starts as", which describes the mechanism rather than the
// job, and wore a near-grey cyan. Between them, the feature went unnoticed.
export const PREFILL_LABEL = "Pre-fill value";

export const prefillChipCls =
  "shrink-0 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-500/20 border border-blue-400/45 text-[10px] font-semibold uppercase tracking-wide text-blue-200";

// What the chip's tooltip says, wherever the chip appears.
export const PREFILL_HINT =
  "Printed on the blank form already — leave it empty for a blank line to write on.";

// A "default value" only means free text for a type that's actually written
// in (Text, Number, Date, …). For a type with fixed answer choices, a free
// text box let a coordinator type something that didn't match any choice —
// and the preview then replaced the tick-boxes with that stray text entirely,
// which is exactly the confusing "default overwrote my choices" behaviour.
// Instead, a choice type's default is one of its OWN choices, pre-checked.
// Returns null for a free-text type, or the list of pickable choices
// (possibly empty, e.g. a Select with no options yet) for a choice type.
export const defaultChoicesFor = (piece) => {
  if (piece.type === "YesNo/Maybe") return ["Yes", "No", "N/A"];
  if (piece.type === "Checkbox") return ["Checked"];
  if (piece.type === "Select" || piece.type === "MultiSelect") {
    return (piece.options || []).filter(Boolean);
  }
  return null;
};

/** One piece/column's default-value control — chips for a choice type, a
 * plain text box for everything else. A single choice (Dropdown, Yes/No/Maybe,
 * Checkbox) only ever pre-selects ONE chip. A checklist (MultiSelect) is the
 * one type that differs — the whole point of a checklist is picking several
 * boxes, so its default can pre-check several too, not just one. */
export default function DefaultValueInput({
  piece,
  value,
  onChange,
  placeholder,
  // Overrides the input's styling. The question builder passes its cyan
  // "starts as" look so every optional starting value is the same colour
  // wherever it appears; the table grid leaves it alone, since a cell there is
  // already inside its own bordered grid.
  tone,
}) {
  const choices = defaultChoicesFor(piece);
  const isMulti = piece.type === "MultiSelect";
  if (choices === null) {
    return (
      <input
        className={`${tone || inputCls} !py-1.5 text-xs`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? "default (optional)"}
      />
    );
  }
  if (!choices.length) {
    return <p className="text-[11px] text-slate-500 italic">Add choices above first.</p>;
  }
  const selected = isMulti ? (Array.isArray(value) ? value : []) : null;
  return (
    <div className="flex flex-wrap gap-1">
      {choices.map((c) => {
        const on = isMulti ? selected.includes(c) : value === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() =>
              isMulti
                ? onChange(on ? selected.filter((x) => x !== c) : [...selected, c])
                : onChange(on ? "" : c)
            }
            className={`px-2 py-1 rounded-md text-[11px] border transition ${
              on
                ? "bg-cyan-500/25 border-cyan-300/50 text-white"
                : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.09]"
            }`}
          >
            {piece.type === "Checkbox" ? (on ? "☑ Pre-checked" : "☐ Pre-checked") : c}
          </button>
        );
      })}
    </div>
  );
}
